import * as React from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@modules/auth/AuthProvider'
import {
  claimVaktScanSessionShift,
  getVaktScanSessionShifts,
  type VaktScanShiftOption,
  type VaktShiftClaim,
} from '@services/api'
import { PortalBrand } from '@shared/Brand'
import { canOpenVakt } from './access'

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function scanSessionTokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('scan_session') ?? ''
}

function formatClockTime(value: string): string {
  const timestamp = Date.parse(value)
  if (!Number.isFinite(timestamp)) return value
  return new Intl.DateTimeFormat('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp))
}

function formatLocalDate(value: string): string {
  const timestamp = Date.parse(`${value}T00:00:00`)
  if (!Number.isFinite(timestamp)) return value
  return new Intl.DateTimeFormat('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(timestamp))
}

export default function VaktScanSessionPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [claim, setClaim] = React.useState<VaktShiftClaim | null>(null)
  const [shifts, setShifts] = React.useState<VaktScanShiftOption[]>([])
  const [claimingShiftId, setClaimingShiftId] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const sessionToken = React.useMemo(() => scanSessionTokenFromUrl(), [])

  React.useEffect(() => {
    if (user) return
    navigate({ to: '/login', search: { next: currentPath() }, replace: true })
  }, [navigate, user])

  React.useEffect(() => {
    if (!user) return

    if (!sessionToken) {
      setLoading(false)
      setError('Lenken mangler scan-session. Skann ny QR-kode fra infoskjermen.')
      return
    }

    if (!canOpenVakt(user)) {
      setLoading(false)
      setError('Brukeren er logget inn, men har ikke tilgang til vaktflaten.')
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    getVaktScanSessionShifts(sessionToken)
      .then((result) => {
        if (cancelled) return
        setShifts(result)
        if (result.length === 0) {
          setError('Ingen vakter er aktive eller starter innen en time.')
        }
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(err?.message || 'Kunne ikke hente vakter for QR-scannen.')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [sessionToken, user])

  async function claimShift(shiftInstanceId: number) {
    setClaimingShiftId(shiftInstanceId)
    setError(null)
    try {
      const result = await claimVaktScanSessionShift(sessionToken, shiftInstanceId)
      setClaim(result)
    } catch (err: any) {
      setError(err?.message || 'Kunne ikke koble brukeren til vakten.')
    } finally {
      setClaimingShiftId(null)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 } }}>
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack spacing={3}>
            <PortalBrand center stacked />

            {loading && (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <CircularProgress />
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
                  Henter vakter
                </Typography>
              </Stack>
            )}

            {!loading && claim && (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <CheckCircleIcon color="success" sx={{ fontSize: 44 }} />
                <Stack spacing={0.5}>
                  <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
                    Vakt koblet
                  </Typography>
                  <Typography color="text.secondary">{claim.shift_label}</Typography>
                </Stack>
                <Button href={claim.checklist_url} variant="contained">
                  Åpne vakt
                </Button>
              </Stack>
            )}

            {!loading && !claim && shifts.length > 0 && (
              <Stack spacing={2}>
                <Stack spacing={0.5} textAlign="center">
                  <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
                    Velg vakt
                  </Typography>
                  <Typography color="text.secondary">
                    QR-koden er verifisert. Velg vakten du skal sjekke inn på.
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  {shifts.map((shift) => (
                    <Paper key={shift.shift_instance_id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.25}>
                          <Typography sx={{ fontWeight: 800 }}>{shift.shift_label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatLocalDate(shift.local_date)} · {formatClockTime(shift.start_at_utc)} -{' '}
                            {formatClockTime(shift.end_at_utc)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {shift.checklist_count} sjekkliste(r) · {shift.task_count} oppgave(r)
                          </Typography>
                        </Stack>
                        <Button
                          variant="contained"
                          endIcon={<ChevronRightIcon />}
                          disabled={claimingShiftId !== null}
                          onClick={() => void claimShift(shift.shift_instance_id)}
                        >
                          {claimingShiftId === shift.shift_instance_id ? 'Kobler...' : 'Velg'}
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            )}

            {!loading && error && (
              <Stack spacing={2} alignItems="center" textAlign="center">
                <ErrorOutlineIcon color="error" sx={{ fontSize: 44 }} />
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700 }}>
                  Kunne ikke koble vakt
                </Typography>
                <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                  {error}
                </Alert>
                <Button href="/" variant="outlined">
                  Til portal
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}
